import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { RedirectedProductListEntry } from 'contracts/redirected-products';
import type { RevertRedirectedProductsPayload } from 'contracts/redirected-products';
import { Drawer } from 'components/ui/drawer';
import { Form } from 'components/ui/form';
import { Button } from 'components/ui/ButtonGs';
import { ActivityIndicator } from 'styles/components/activityIndicators';
import { ProductsTable } from 'pages/stock-inspection/shared-components';

import { formSchema, type FormSchema } from './form-schema';

export type RevertRedirectedProductsDrawerRef = {
  open: (products: RedirectedProductListEntry[]) => void;
  close: () => void;
}

type Props = {
  onSubmit: (data: RevertRedirectedProductsPayload) => void;
  loading?: boolean;
}

export const RevertRedirectedProductsDrawer
  = React.forwardRef<RevertRedirectedProductsDrawerRef, Props>(({
                                                                  onSubmit,
                                                                  loading,
                                                                }, ref) => {
  const [products, setProducts] = React.useState<RedirectedProductListEntry[]>([]);
  const [open, setOpen] = React.useState(false);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const onFormSubmit = React.useCallback((formData: FormSchema) => {
    onSubmit({
      productIds: products.map(p => p.id),
    });
  }, [onSubmit, products]);

  const onOpenChange = React.useCallback((open: boolean) => {
    setOpen(open);
    form.reset();
  }, [form]);

  React.useImperativeHandle(ref, () => ({
    open: (selected) => {
      onOpenChange(selected.length > 0);
      setProducts(selected);
    },
    close: () => {
      onOpenChange(false);
      setProducts([]);
    },
  }), [onOpenChange]);

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Content>
        <div className="max-w-[800px] w-full mx-auto">
          <Drawer.Header>
            <Drawer.Title className="text-center flex flex-col font-GilroyBold text-gray-600 text-[16px]">
              <span>Produtos selecionados</span>
              <span className={'font-extrabold text-gray-800 text-[40px]'}>
                {products.length.toString().padStart(3, '0')}
              </span>
            </Drawer.Title>
          </Drawer.Header>
          <ProductsTable products={products} className={'max-h-[260px] overflow-y-auto mb-6'} />
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onFormSubmit)}
              className="flex flex-col gap-4 pb-10"
            >
              <div className={'grid grid-cols-2 gap-4'}>
                <Button.Root
                  type={'button'}
                  size={'sm'}
                  variant={'secondary'}
                  onClick={() => onOpenChange(false)}
                >
                  Cancelar
                </Button.Root>
                <Button.Root type={'submit'} size={'sm'} disabled={loading}>
                  {loading ? <ActivityIndicator /> : 'Salvar'}
                </Button.Root>
              </div>
            </form>
          </Form>
        </div>
      </Drawer.Content>
    </Drawer.Root>
  );
});
RevertRedirectedProductsDrawer.displayName = 'RevertRedirectedProductsDrawer';